// Field specs for catalog parts, shared by the inline "Add new" dialog and the
// Catalog manage page. Mirrors the backend Create*Request schemas — brand and
// name are the only required fields; everything else is optional spec data that
// improves the model. `type: "number"` fields are sent as numbers (blank omitted);
// "bool" renders a switch; "select" renders a dropdown from `options`.

export const PART_TYPES = ["motor", "propeller", "esc", "battery"];

export const PART_META = {
  motor: { label: "Motor", plural: "Motors", catalogKey: "motors" },
  propeller: { label: "Propeller", plural: "Propellers", catalogKey: "propellers" },
  esc: { label: "ESC", plural: "ESCs", catalogKey: "escs" },
  battery: { label: "Battery", plural: "Batteries", catalogKey: "batteries" },
};

const COOLING_OPTIONS = ["excellent", "good", "medium", "poor", "very poor"];

export const PART_FIELDS = {
  motor: [
    { key: "brand", label: "Brand", type: "text", required: true },
    { key: "name", label: "Name / model", type: "text", required: true },
    { key: "kv", label: "Kv (rpm/V)", type: "number" },
    { key: "resistance_ohm", label: "Resistance Rm (Ω)", type: "number" },
    { key: "no_load_current_a", label: "No-load current I₀ (A)", type: "number" },
    { key: "no_load_voltage_v", label: "Voltage at I₀ (V)", type: "number" },
    { key: "max_current_a", label: "Max current (A)", type: "number" },
    { key: "max_power_w", label: "Max power (W)", type: "number" },
    { key: "poles", label: "Magnetic poles", type: "number" },
    { key: "stator_size", label: "Stator size (e.g. 2207)", type: "text" },
    { key: "case_length_mm", label: "Case length (mm)", type: "number" },
    { key: "weight_g", label: "Weight (g)", type: "number" },
    { key: "cooling", label: "Cooling", type: "select", options: COOLING_OPTIONS },
    { key: "source", label: "Source", type: "text" },
    { key: "datasheet_url", label: "Datasheet / reference URL", type: "text" },
  ],
  propeller: [
    { key: "brand", label: "Brand", type: "text", required: true },
    { key: "name", label: "Name / model", type: "text", required: true },
    { key: "diameter_in", label: "Diameter (in)", type: "number" },
    { key: "pitch_in", label: "Pitch (in)", type: "number" },
    { key: "blades", label: "Blades", type: "number" },
    { key: "material", label: "Material", type: "text" },
    { key: "weight_g", label: "Weight (g)", type: "number" },
    { key: "source", label: "Source", type: "text" },
  ],
  esc: [
    { key: "brand", label: "Brand", type: "text", required: true },
    { key: "name", label: "Name / model", type: "text", required: true },
    { key: "cont_current_a", label: "Cont. current (A)", type: "number" },
    { key: "max_current_a", label: "Max current (A)", type: "number" },
    { key: "resistance_ohm", label: "Resistance (Ω)", type: "number" },
    { key: "weight_g", label: "Weight (g)", type: "number" },
    { key: "protocol", label: "Protocol", type: "text" },
    { key: "bec", label: "Has BEC", type: "bool" },
    { key: "source", label: "Source", type: "text" },
  ],
  battery: [
    { key: "brand", label: "Brand", type: "text", required: true },
    { key: "name", label: "Name / model", type: "text", required: true },
    { key: "chemistry", label: "Chemistry", type: "text" },
    { key: "capacity_mah", label: "Capacity (mAh)", type: "number" },
    { key: "c_rating_cont", label: "C-rating (cont.)", type: "number" },
    { key: "c_rating_max", label: "C-rating (max)", type: "number" },
    { key: "resistance_ohm", label: "Resistance (Ω)", type: "number" },
    { key: "nominal_voltage_v", label: "Nominal voltage / cell (V)", type: "number" },
    { key: "weight_g", label: "Weight (g)", type: "number" },
    { key: "source", label: "Source", type: "text" },
  ],
  fcu: [
    { key: "brand", label: "Brand", type: "text", required: true },
    { key: "name", label: "Name / model", type: "text", required: true },
    { key: "power_draw_w", label: "Power draw (W)", type: "number" },
    { key: "weight_g", label: "Weight (g)", type: "number" },
    { key: "temp_min_c", label: "Operating temp min (°C)", type: "number" },
    { key: "temp_max_c", label: "Operating temp max (°C)", type: "number" },
    { key: "input_voltage_v", label: "Input voltage (V)", type: "number" },
    { key: "firmware", label: "Firmware (e.g. ArduPilot, PX4, Betaflight)", type: "text" },
    { key: "source", label: "Source", type: "text" },
  ],
  sensor: [
    { key: "brand", label: "Brand", type: "text", required: true },
    { key: "name", label: "Name / model", type: "text", required: true },
    { key: "sensor_type", label: "Type", type: "select", options: ["GPS", "Rangefinder", "Camera", "Optical flow", "Telemetry", "IMU", "Other"] },
    { key: "power_draw_w", label: "Power draw (W)", type: "number" },
    { key: "weight_g", label: "Weight (g)", type: "number" },
    { key: "temp_min_c", label: "Operating temp min (°C)", type: "number" },
    { key: "temp_max_c", label: "Operating temp max (°C)", type: "number" },
    { key: "interface", label: "Interface (UART, I²C, USB…)", type: "text" },
    { key: "source", label: "Source", type: "text" },
  ],
};

// Drone (airframe) fields. Motors are associated separately via a multi-select,
// so they are not part of this spec.
export const DRONE_FIELDS = [
  { key: "brand", label: "Brand", type: "text", required: true },
  { key: "name", label: "Name / model", type: "text", required: true },
  { key: "drone_class", label: "Class (e.g. 5-inch FPV)", type: "text" },
  { key: "num_motors", label: "# Motors", type: "number" },
  { key: "frame_size_mm", label: "Frame size (mm)", type: "number" },
  { key: "prop_size_in", label: "Prop size (in)", type: "number" },
  { key: "auw_g", label: "All-up weight (g)", type: "number" },
  { key: "cells_series", label: "Cells (S)", type: "number" },
  { key: "coaxial", label: "Coaxial", type: "bool" },
  { key: "notes", label: "Notes", type: "text" },
  { key: "source", label: "Source", type: "text" },
];

// Build a POST payload from raw form state against a field spec: required text
// kept as-is, numbers coerced, empty strings dropped so the backend stores NULL.
export const buildPayloadFromFields = (fields, values) => {
  const payload = {};
  for (const field of fields) {
    const raw = values[field.key];
    if (field.type === "bool") {
      if (raw !== undefined) payload[field.key] = Boolean(raw);
      continue;
    }
    if (raw === undefined || raw === null || String(raw).trim() === "") continue;
    payload[field.key] = field.type === "number" ? Number(raw) : String(raw).trim();
  }
  return payload;
};

export const buildPartPayload = (type, values) => buildPayloadFromFields(PART_FIELDS[type], values);
